import { Box, ButtonBase, IconButton, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { ChevronLeft, ChevronRight, GetApp, InsertDriveFileOutlined, ZoomIn, ZoomOut } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { Button, CircularLoader, SelectField } from '@roc/ui';
import { downloadDocument } from '@roc/feature-utils';
import { PostQcReviewStore, QcFile } from './postQcReviewStore';
import { qcHiddenScrollbar, qcPalette } from './postQcPalette';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0,
    backgroundColor: qcPalette.sunken,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 52,
    padding: theme.spacing(0.5, 1.5, 0.5, 2),
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${qcPalette.border}`,
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: 600,
    color: qcPalette.ink,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  picker: {
    flex: 1,
    minWidth: 0,
  },
  pager: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    fontSize: 12,
    color: qcPalette.muted,
    fontVariantNumeric: 'tabular-nums',
  },
  divider: {
    width: 1,
    height: 20,
    backgroundColor: qcPalette.border,
  },
  zoomValue: {
    minWidth: 40,
    height: 28,
    padding: '0 6px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    color: qcPalette.inkSoft,
    fontVariantNumeric: 'tabular-nums',
  },
  canvas: {
    ...qcHiddenScrollbar,
    position: 'relative',
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: theme.spacing(2.5),
  },
  page: {
    borderRadius: 4,
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(20,28,45,0.08), 0 8px 24px rgba(20,28,45,0.08)',
  },
  messageWrap: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(2.5),
  },
  message: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.25),
    padding: theme.spacing(3),
    textAlign: 'center',
    color: qcPalette.muted,
    border: `1.5px dashed ${qcPalette.remaining}`,
    borderRadius: 12,
  },
  messageTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: qcPalette.ink,
  },
  messageBody: {
    fontSize: 13,
    maxWidth: 300,
  },
}));

const contentTypeOf = (headers: Record<string, string>): string => {
  const entry = Object.keys(headers ?? {}).find(name => name.toLowerCase() === 'content-type');
  return entry ? String(headers[entry]).toLowerCase() : '';
};

const isPdf = (file: QcFile, headers: Record<string, string>) => {
  const contentType = contentTypeOf(headers);
  if (contentType && !contentType.startsWith('application/octet-stream')) {
    return contentType.includes('application/pdf');
  }
  return /\.pdf$/i.test(file?.fileName ?? '');
};

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const CANVAS_PADDING = 40;

export const PostQcDocumentViewer = observer(({ store }: { store: PostQcReviewStore }) => {
  const classes = useStyles();
  const section = store.expandedSectionModel;
  const documentId = section ? store.selectedFileId(section) : null;
  const file = section?.documents.find(document => document.documentId === documentId) ?? null;
  const fileState = section && documentId ? store.fileState(section, documentId) : undefined;
  const [scale, setScale] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [renderError, setRenderError] = useState(false);
  const [paneWidth, setPaneWidth] = useState(0);
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = rootRef.current;
    const Observer = (window as any).ResizeObserver;
    if (!element || !Observer) {
      return;
    }
    const observer = new Observer(entries => setPaneWidth(Math.floor(entries[0].contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const pageWidth = paneWidth > 0 ? Math.max(200, Math.round((paneWidth - CANVAS_PADDING) * scale)) : undefined;
  const zoom = (step: number) => setScale(current => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((current + step) * 10) / 10)));

  useEffect(() => {
    setPage(1);
    setNumPages(0);
    setRenderError(false);
    if (section && documentId) {
      store.loadFile(section, documentId);
    }
  }, [section?.sectionName, documentId]);

  const pdfSource = useMemo(
    () => (fileState && fileState.status === 'loaded' ? { data: fileState.data.slice(0) } : null),
    [fileState]
  );

  const download = () => {
    if (fileState && fileState.status === 'loaded') {
      downloadDocument(fileState.data, { ...fileState.headers }, 'download', file?.fileName);
    }
  };

  const retry = () => {
    setRenderError(false);
    if (section && documentId) {
      store.loadFile(section, documentId);
    }
  };

  const renderMessage = (title: string, body?: string, actions?: JSX.Element) => (
    <div className={classes.messageWrap}>
      <div className={classes.message}>
        <InsertDriveFileOutlined style={{ fontSize: 36 }} />
        <Typography className={classes.messageTitle}>{title}</Typography>
        {body && <Typography className={classes.messageBody}>{body}</Typography>}
        {actions}
      </div>
    </div>
  );

  const renderBody = () => {
    if (!section) {
      return renderMessage('No document selected', 'Pick a document on the left to see its file.');
    }
    if (!file) {
      return renderMessage(
        'No file linked to this section',
        'Review these fields against the documents on the loan, or ask the closing team to upload the file.'
      );
    }
    if (!fileState || fileState.status === 'loading') {
      return (
        <Box className={classes.canvas}>
          <CircularLoader position="absolute" />
        </Box>
      );
    }
    if (fileState.status === 'error' || renderError) {
      return renderMessage(
        "File couldn't be loaded",
        file.fileName,
        <Box display="flex" style={{ gap: 8 }}>
          <Button testId="post-qc-file-retry" variant="outlined" color="primary" onClick={retry}>
            Retry
          </Button>
          {fileState.status === 'loaded' && (
            <Button testId="post-qc-file-download" variant="outlined" color="primary" onClick={download}>
              Download
            </Button>
          )}
        </Box>
      );
    }
    if (!isPdf(file, fileState.headers)) {
      return renderMessage(
        "Preview isn't available for this file type",
        file.fileName,
        <Button testId="post-qc-file-download" variant="outlined" color="primary" onClick={download}>
          Download
        </Button>
      );
    }
    return (
      <Box className={classes.canvas}>
        <Document
          file={pdfSource}
          onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
          onLoadError={() => setRenderError(true)}
          loading={<CircularLoader position="absolute" />}
        >
          <Page className={classes.page} pageNumber={page} width={pageWidth} />
        </Document>
      </Box>
    );
  };

  return (
    <section aria-label="Document preview" className={classes.root} ref={rootRef}>
      <div className={classes.toolbar}>
        {section && section.documents.length > 1 ? (
          <div className={classes.picker}>
            <SelectField
              testId="post-qc-file"
              variant="outlined"
              standaloneLabel
              fullWidth
              value={documentId}
              options={section.documents.map(document => ({ label: document.fileName, value: document.documentId }))}
              onChange={value => store.selectFile(section, Number(value))}
            />
          </div>
        ) : (
          <span className={classes.title} title={file?.fileName ?? ''}>
            {file?.fileName ?? 'No file linked'}
          </span>
        )}
        {numPages > 0 && !renderError && (
          <>
            <div className={classes.pager}>
              <IconButton size="small" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
                <ChevronLeft fontSize="small" />
              </IconButton>
              {page} / {numPages}
              <IconButton size="small" disabled={page >= numPages} onClick={() => setPage(page + 1)} aria-label="Next page">
                <ChevronRight fontSize="small" />
              </IconButton>
            </div>
            <span className={classes.divider} />
            <div className={classes.pager}>
              <IconButton size="small" disabled={scale <= MIN_ZOOM} onClick={() => zoom(-0.1)} aria-label="Zoom out">
                <ZoomOut fontSize="small" />
              </IconButton>
              <ButtonBase className={classes.zoomValue} onClick={() => setScale(1)} title="Fit to width">
                {scale === 1 ? 'Fit' : `${Math.round(scale * 100)}%`}
              </ButtonBase>
              <IconButton size="small" disabled={scale >= MAX_ZOOM} onClick={() => zoom(0.1)} aria-label="Zoom in">
                <ZoomIn fontSize="small" />
              </IconButton>
            </div>
          </>
        )}
        <Tooltip title="Download">
          <span>
            <IconButton size="small" disabled={!fileState || fileState.status !== 'loaded'} onClick={download} aria-label="Download file">
              <GetApp fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      {renderBody()}
    </section>
  );
});
